import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ActionPlanBoardWidget from "../../components/ActionPlan/ActionPlanBoardWidget";
import ActionPlanSummaryWidget from "../../components/ActionPlan/ActionPlanSummaryWidget";
import {
    PoleScoreDetailsWidget,
    ServiceScoreDetailsWidget
} from "../../components/Overview/OverviewWidgets";
import ValeursPage from "../../components/ValeursPage/ValeursPage";
import {
    OverviewScope,
    PoleDashboardViewModel,
    VALUE_CODES,
    ValueCode
} from "../../models/OverviewDashboard";
import { calculateDecisionSuggestion } from "../../services/DecisionSupportService";
import OrganizationService, { OrganizationNode } from "../../services/OrganizationService";
import OverviewDashboardService from "../../services/OverviewDashboardService";
import { getCurrentCalendarMonthPeriod } from "../../utils/StatisticsPeriod";
import { getUpCareTheme, UPCARE_THEMES, UpCareThemeCode } from "../../utils/UpCareThemes";
import styles from "./PratiquePage.module.css";

const DEFAULT_POLE_IDENTIFIER = "CHU-NANCY";
const MEASURE_ID = "Meas-UpCareValeurometreByService";
type RequestScope = "all" | "mine";

const PratiquePage: FunctionComponent = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dashboard, setDashboard] = useState<PoleDashboardViewModel>();
    const [availablePoles, setAvailablePoles] = useState<OrganizationNode[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const fhirBaseUrl = process.env.REACT_APP_FHIR_URL ?? "fhir";
    const requestedValue = searchParams.get("value");
    const selectedValue: ValueCode = VALUE_CODES.includes(requestedValue as ValueCode)
        ? requestedValue as ValueCode
        : "bienveillance";
    const requestedTheme = searchParams.get("theme");
    const selectedTheme: UpCareThemeCode | undefined = getUpCareTheme(requestedTheme ?? undefined)?.code;
    const selectedPoleIdentifier = searchParams.get("pole") ?? DEFAULT_POLE_IDENTIFIER;
    const requestScope: RequestScope = searchParams.get("requests") === "mine" ? "mine" : "all";
    const scope: OverviewScope = searchParams.get("scope") === "services" ? "services" : "pole";
    const selectedOrganization = searchParams.get("organization");
    const statisticsPeriod = useMemo(() => getCurrentCalendarMonthPeriod(), []);

    const dashboardService = useMemo(() => new OverviewDashboardService({
        fhirBaseUrl,
        measureId: MEASURE_ID,
        periodStart: statisticsPeriod.periodStart,
        periodEnd: statisticsPeriod.periodEnd
    }), [fhirBaseUrl, statisticsPeriod]);

    const organizationService = useMemo(
        () => new OrganizationService({ fhirBaseUrl }),
        [fhirBaseUrl]
    );

    useEffect(() => {
        let mounted = true;

        const loadPoles = async () => {
            try {
                const roots = await organizationService.searchInternalOrganizationTree();
                let poles = roots;

                if (!roots.some((item) => item.id === DEFAULT_POLE_IDENTIFIER)) {
                    try {
                        const defaultPole = await organizationService.getOrganizationByIdentifier(DEFAULT_POLE_IDENTIFIER);
                        poles = [defaultPole, ...roots];
                    } catch (reason) {
                        console.error(reason);
                    }
                }

                if (mounted) {
                    setAvailablePoles(
                        poles
                            .filter((item, index, items) => items.findIndex((candidate) => candidate.reference === item.reference) === index)
                            .sort((left, right) => left.name.localeCompare(right.name, "fr"))
                    );
                }
            } catch (reason) {
                console.error("Impossible de charger la liste des pôles.", reason);
            }
        };

        loadPoles();

        return () => {
            mounted = false;
        };
    }, [organizationService]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(undefined);

        dashboardService.load(selectedPoleIdentifier)
            .then((result) => {
                if (mounted) setDashboard(result);
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) setError("Impossible de charger le contexte du plan d'action.");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [dashboardService, selectedPoleIdentifier]);

    const selectedService = dashboard?.services.find((item) =>
        item.service.reference === selectedOrganization
    );
    const effectiveScope: OverviewScope = scope === "services" && selectedService ? "services" : "pole";
    const selectedScore = effectiveScope === "services"
        ? selectedService?.values[selectedValue].score
        : dashboard?.poleScores[selectedValue].score;
    const suggestion = calculateDecisionSuggestion(selectedValue, selectedScore);

    const organizationReferences = effectiveScope === "services" && selectedService
        ? [selectedService.service.reference]
        : dashboard
            ? [dashboard.pole.reference, ...dashboard.services.map((item) => item.service.reference)]
            : [];

    const myPoleRequestReferences = dashboard
        ? [dashboard.pole.reference, ...dashboard.services.map((item) => item.service.reference)]
        : [];
    const myPoleRequestKey = myPoleRequestReferences.join("|");

    const requestOrganizationReferences = useMemo(() => {
        if (requestScope === "mine") {
            return myPoleRequestReferences;
        }

        const allReferences = availablePoles.flatMap(flattenOrganizationReferences);
        return Array.from(new Set([...allReferences, ...myPoleRequestReferences]));
    }, [requestScope, availablePoles, myPoleRequestKey]);

    const handleValueChange = (value: ValueCode) => {
        const next = new URLSearchParams(searchParams);
        next.set("value", value);
        setSearchParams(next);
    };

    const handleThemeChange = (theme?: UpCareThemeCode) => {
        const next = new URLSearchParams(searchParams);
        if (theme) {
            next.set("theme", theme);
        } else {
            next.delete("theme");
        }
        setSearchParams(next);
    };

    const handleRequestScopeChange = (nextScope: RequestScope) => {
        const next = new URLSearchParams(searchParams);
        if (nextScope === "all") {
            next.delete("requests");
        } else {
            next.set("requests", "mine");
        }
        setSearchParams(next);
    };

    const contextName = effectiveScope === "services" && selectedService
        ? selectedService.service.name
        : dashboard?.pole.name ?? "Pôle";

    return (
        <ValeursPage titleKey="title.pratique" loading={false} needsLogin={true}>
            <div className={styles.page}>
                {loading && <p className={styles.state}>Chargement du plan d'action…</p>}
                {error && <p className={styles.error}>{error}</p>}

                {dashboard && !loading && !error && (
                    <div className={styles.layout}>
                        <main className={styles.mainColumn}>
                            <header className={styles.header}>
                                <div>
                                    <h1>Accompagnement</h1>
                                    <p>
                                        {requestScope === "all"
                                            ? "Besoins et solutions de tous les pôles"
                                            : `Besoins et solutions de ${dashboard.pole.name}`}
                                        {` · ateliers et statistiques : ${contextName}`}
                                    </p>
                                </div>
                                <div className={styles.requestScopeControl} role="group" aria-label="Périmètre des besoins et solutions">
                                    <button
                                        type="button"
                                        className={requestScope === "all" ? styles.activeScope : undefined}
                                        aria-pressed={requestScope === "all"}
                                        onClick={() => handleRequestScopeChange("all")}
                                    >
                                        Voir tous les pôles
                                    </button>
                                    <button
                                        type="button"
                                        className={requestScope === "mine" ? styles.activeScope : undefined}
                                        aria-pressed={requestScope === "mine"}
                                        onClick={() => handleRequestScopeChange("mine")}
                                    >
                                        Voir mon pôle uniquement
                                    </button>
                                </div>
                            </header>

                            <section className={styles.themeFilter} aria-label="Filtrer par thématique">
                                <span>Thématique</span>
                                <div>
                                    <button
                                        type="button"
                                        className={!selectedTheme ? styles.activeTheme : undefined}
                                        aria-pressed={!selectedTheme}
                                        onClick={() => handleThemeChange(undefined)}
                                    >
                                        Toutes
                                    </button>
                                    {UPCARE_THEMES.map((theme) => (
                                        <button
                                            key={theme.code}
                                            type="button"
                                            className={selectedTheme === theme.code ? styles.activeTheme : undefined}
                                            aria-pressed={selectedTheme === theme.code}
                                            onClick={() => handleThemeChange(theme.code)}
                                        >
                                            {theme.display}
                                        </button>
                                    ))}
                                </div>
                            </section>

                            <section className={styles.decisionCard}>
                                <span>Aide à la décision V0</span>
                                <strong>{suggestion.title}</strong>
                                <p>{suggestion.description}</p>
                            </section>

                            <ActionPlanBoardWidget
                                fhirBaseUrl={fhirBaseUrl}
                                value={selectedValue}
                                theme={selectedTheme}
                                organizationReferences={organizationReferences}
                                requestOrganizationReferences={requestOrganizationReferences}
                                suggestion={suggestion}
                                poleOrganization={{
                                    reference: dashboard.pole.reference,
                                    name: dashboard.pole.name
                                }}
                            />
                        </main>

                        <aside className={styles.rightColumn}>
                            {effectiveScope === "services" ? (
                                <ServiceScoreDetailsWidget
                                    service={selectedService}
                                    selectedValue={selectedValue}
                                    onValueChange={handleValueChange}
                                />
                            ) : (
                                <PoleScoreDetailsWidget
                                    dashboard={dashboard}
                                    selectedValue={selectedValue}
                                    onValueChange={handleValueChange}
                                />
                            )}

                            <ActionPlanSummaryWidget
                                fhirBaseUrl={fhirBaseUrl}
                                value={selectedValue}
                                theme={selectedTheme}
                                organizationReferences={organizationReferences}
                                poleOrganization={{
                                    reference: dashboard.pole.reference,
                                    name: dashboard.pole.name
                                }}
                            />
                        </aside>
                    </div>
                )}
            </div>
        </ValeursPage>
    );
};

const flattenOrganizationReferences = (organization: OrganizationNode): string[] => [
    organization.reference,
    ...organization.children.flatMap(flattenOrganizationReferences)
];

export default PratiquePage;
